import { useMutation, useQueryClient } from "react-query";

import { updateTarget } from "api/targets";
import type { Target } from "api/types/aba";
import { useGlobalToast } from "components/GlobalToastProvider";
import { GET_GOAL_BY_ID, GET_LIST_OF_TARGETS, GET_TARGET_BY_ID } from "@constants/reactQueryKeys";

interface UpdateTargetPayload {
  targetId: number;
  goalId: number;
  targetData: Partial<Target>;
}

/**
 * Update an existing target. Invalidates the target detail, the target list,
 * AND the parent goal detail (`GET_GOAL_BY_ID`).
 */
const useUpdateTarget = (options: any = {}) => {
  const { onSuccess = () => {}, onError = () => {}, ...otherOptions } = options;
  const queryClient = useQueryClient();
  const { showToast } = useGlobalToast();

  const {
    mutate: updateTargetAction,
    isLoading: isUpdatingTarget,
    ...rest
  } = useMutation(
    ({ targetId, targetData }: UpdateTargetPayload) => updateTarget(targetId, targetData),
    {
      onSuccess: (response, variables) => {
        showToast({ message: "Target updated successfully" });
        queryClient.invalidateQueries([GET_TARGET_BY_ID, variables.targetId]);
        queryClient.invalidateQueries([GET_LIST_OF_TARGETS, variables.goalId]);
        queryClient.invalidateQueries([GET_GOAL_BY_ID, variables.goalId]);
        onSuccess(response, variables);
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error || "Failed to update target";
        showToast({ message: errorMessage, errorState: true });
        onError(error);
      },
      ...otherOptions,
    }
  );

  return { updateTargetAction, isUpdatingTarget, ...rest };
};

export default useUpdateTarget;
